import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, Menu, Moon, Sun } from "lucide-react";

import Button from "../marketing/components/Button";
import Container from "../marketing/components/Container";
import { scrollToSection } from "../marketing/utils/scrollToSection";
import freightKitLogo from "../assets/logo/freightkitlogoExtended.png";
import { useDarkMode } from "../context/DarkModeContext";

const CALENDLY_URL = "https://calendly.com/shawak-vantagecybertech/30min";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  const isAuth = location.pathname === "/loginPage" || location.pathname === "/register";

  const navItems = useMemo(
    () => [
      { label: "Product tour", section: "tour" },
      { label: "Features", section: "features" },
      { label: "Use cases", section: "use-cases" },
      { label: "Pricing", section: "pricing" },
      { label: "FAQ", section: "faq" },
    ],
    []
  );

  const goToSection = (sectionId) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      window.setTimeout(() => scrollToSection(sectionId), 60);
      return;
    }
    scrollToSection(sectionId);
  };

  // Keep a single source of truth for header height (used for layout + momentum scroll offset)
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const setVar = () => {
      const h = Math.ceil(el.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty("--fk-header-h", `${h}px`);
    };

    setVar();
    const ro = new ResizeObserver(() => setVar());
    ro.observe(el);
    window.addEventListener("resize", setVar, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
    };
  }, []);

  return (
    <div
      ref={headerRef}
      data-fk-header
      className="fk-theme-smooth fixed inset-x-0 top-0 z-[9999] h-16 border-b border-fk-border shadow-sm backdrop-blur-xl backdrop-saturate-150 transition-colors duration-1000 ease-out motion-reduce:transition-none bg-[#60A5FA]/12 dark:border-white/10 dark:bg-gray-950/60 before:absolute before:inset-0 before:content-[''] before:bg-gradient-to-b before:from-[#60A5FA]/12 before:to-transparent before:pointer-events-none"
    >
      <Container className="h-full py-0">
        <div className="flex h-full items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl px-2 py-1 text-sm font-semibold text-fk-ink hover:opacity-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent focus-visible:ring-offset-2 focus-visible:ring-offset-fk-surface dark:text-gray-100 dark:hover:opacity-[0.98] dark:focus-visible:ring-offset-gray-950"
            >
              <span className="inline-flex h-9 w-[190px] items-center justify-start overflow-hidden rounded-xl sm:h-10 sm:w-[210px]">
                <img
                  src={freightKitLogo}
                  alt="FreightKit"
                  className="h-full w-full object-contain"
                  loading="eager"
                  decoding="async"
                />
              </span>
            </Link>
          </div>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-2 lg:flex" aria-label="Primary">
            {!isAuth ? (
              <>
                {navItems.map((it) => (
                  <button
                    key={it.section}
                    type="button"
                    onClick={() => goToSection(it.section)}
                    className="rounded-xl px-3 py-2 text-sm font-semibold text-fk-ink/90 hover:bg-fk-ink/[0.04] hover:text-fk-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent2 focus-visible:ring-offset-2 focus-visible:ring-offset-fk-surface dark:text-gray-200 dark:hover:bg-white/5 dark:hover:text-gray-100 dark:focus-visible:ring-offset-gray-950"
                  >
                    {it.label}
                  </button>
                ))}
              </>
            ) : null}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden items-center gap-2 lg:flex">
            <button
              type="button"
              onClick={toggleDarkMode}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-fk-border bg-fk-surface/60 text-fk-ink shadow-sm transition hover:bg-fk-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent2 focus-visible:ring-offset-2 focus-visible:ring-offset-fk-surface dark:border-white/10 dark:bg-white/5 dark:text-gray-100 dark:hover:bg-white/10 dark:focus-visible:ring-offset-gray-950"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {!isAuth ? (
              <>
                <Button
                  as="link"
                  to="/loginPage"
                  variant="secondary"
                >
                  Login
                </Button>
                <Button
                  as="a"
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  variant="accent"
                  withArrow
                >
                  Book a demo
                </Button>
              </>
            ) : (
              <Button as="link" to="/" variant="secondary">
                Back to site
              </Button>
            )}
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              type="button"
              onClick={toggleDarkMode}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-fk-border bg-fk-surface/60 text-fk-ink shadow-sm transition hover:bg-fk-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent2 focus-visible:ring-offset-2 focus-visible:ring-offset-fk-surface dark:border-white/10 dark:bg-white/5 dark:text-gray-100 dark:hover:bg-white/10 dark:focus-visible:ring-offset-gray-950"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-fk-border bg-fk-surface/60 text-fk-ink shadow-sm hover:bg-fk-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent2 focus-visible:ring-offset-2 focus-visible:ring-offset-fk-surface dark:border-white/10 dark:bg-white/5 dark:text-gray-100 dark:hover:bg-white/10 dark:focus-visible:ring-offset-gray-950"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {mobileOpen ? (
          <div className="mt-3 rounded-2xl border border-fk-border bg-white p-3 shadow-lg dark:border-white/10 dark:bg-gray-950">
            <nav className="flex flex-col gap-1" aria-label="Mobile primary">
              {!isAuth ? (
                <>
                  {navItems.map((it) => (
                    <button
                      key={it.section}
                      type="button"
                      onClick={() => goToSection(it.section)}
                      className="rounded-xl px-3 py-2 text-left text-sm font-semibold text-fk-ink/90 hover:bg-fk-ink/[0.04] hover:text-fk-ink dark:text-gray-200 dark:hover:bg-white/5 dark:hover:text-gray-100"
                    >
                      {it.label}
                    </button>
                  ))}
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <Button
                      as="link"
                      to="/loginPage"
                      onClick={() => setMobileOpen(false)}
                      variant="secondary"
                      className="w-full justify-center"
                    >
                      Login
                    </Button>
                    <Button
                      as="a"
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noreferrer"
                      variant="accent"
                      withArrow
                      className="w-full justify-center"
                    >
                      Book a demo
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  as="link"
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  variant="secondary"
                  className="w-full justify-center"
                >
                  Back to site
                </Button>
              )}
            </nav>
          </div>
        ) : null}
      </Container>
    </div>
  );
};

export default Header;
